/**
 * movimientoAbonoCredito.ts — Ingreso de caja por abono a cuenta de crédito.
 *
 * Un abono NO es una venta nueva en Ventas Individuales: es cobro de deuda
 * y se registra como movimiento tipo "ingreso" (mismo canal que retiros/gastos).
 */

import type { MovimientoCaja } from "../types/caja.types";
import { txRead, txWrite } from "@/lib/persistence/transactionalStore";

export const CAJA_MOVIMIENTOS_KEY = "caja_movimientos";

/** Campos que usaban el nombre legacy «fiado» en movimientos ya persistidos. */
type MovimientoLegacyCredito = MovimientoCaja & {
  fiadoAbono?: boolean;
  fiadoId?: string;
};

/**
 * Normaliza movimientos persistidos antes del renombre fiado→crédito:
 * fiadoAbono → creditoAbono y fiadoId → creditoId. Idempotente.
 */
export function normalizeMovimientoLegacyCredito(
  mov: MovimientoCaja,
): MovimientoCaja {
  const legacy = mov as MovimientoLegacyCredito;
  if (legacy.fiadoAbono == null && legacy.fiadoId == null) return mov;
  const out: MovimientoCaja = { ...mov };
  if (out.creditoAbono == null && legacy.fiadoAbono != null) {
    out.creditoAbono = legacy.fiadoAbono;
  }
  if (out.creditoId == null && legacy.fiadoId != null) {
    out.creditoId = legacy.fiadoId;
  }
  return out;
}

export interface RegistrarAbonoCreditoMovimientoInput {
  creditoId: string;
  clienteNombre: string;
  monto: number;
  metodoPago: string;
  fecha?: string;
  usuarioId: string;
  userName: string;
  sesionId: string;
  puntoCajaId: string;
  puntoCajaNombre?: string;
}

export function buildMovimientoAbonoCredito(
  input: RegistrarAbonoCreditoMovimientoInput,
): MovimientoCaja {
  const fecha = input.fecha || new Date().toISOString();
  return {
    id: `MOV-AB-${Date.now()}`,
    tipo: "ingreso",
    monto: Math.round(Number(input.monto) || 0),
    motivo: `Abono a crédito: ${input.clienteNombre || "Cliente"}`,
    fecha,
    usuarioId: input.usuarioId,
    userName: input.userName,
    metodoPago: (input.metodoPago ||
      "efectivo") as MovimientoCaja["metodoPago"],
    sesionId: input.sesionId,
    puntoCajaId: input.puntoCajaId,
    puntoCajaNombre: input.puntoCajaNombre,
    estado: "activo",
    creditoAbono: true,
    creditoId: input.creditoId,
  };
}

export function appendMovimientoCaja(mov: MovimientoCaja): void {
  const arr = txRead<MovimientoCaja[]>(CAJA_MOVIMIENTOS_KEY, []);
  txWrite(CAJA_MOVIMIENTOS_KEY, [mov, ...arr]);
}

/** Registra ingreso por abono; requiere sesión de caja abierta. */
export function registerMovimientoAbonoCredito(
  input: RegistrarAbonoCreditoMovimientoInput,
): MovimientoCaja | null {
  if (!input.sesionId?.trim()) return null;
  const mov = buildMovimientoAbonoCredito(input);
  appendMovimientoCaja(mov);
  return mov;
}

export function notifyCajaMovimientosUpdated(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("caja-movimientos-updated"));
}

export function readMovimientosCajaLocal(): MovimientoCaja[] {
  return txRead<MovimientoCaja[]>(CAJA_MOVIMIENTOS_KEY, []);
}

/** Une API + localStorage por id (el mock puede devolver lista anterior al backfill). */
export function mergeMovimientosCaja(
  fromApi: MovimientoCaja[],
  fromLocal: MovimientoCaja[],
): MovimientoCaja[] {
  const byId = new Map<string, MovimientoCaja>();
  for (const m of fromApi) byId.set(m.id, normalizeMovimientoLegacyCredito(m));
  for (const m of fromLocal)
    byId.set(m.id, normalizeMovimientoLegacyCredito(m));
  return [...byId.values()].sort(
    (a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime(),
  );
}

export function isMovimientoAbonoCredito(
  m: Pick<MovimientoCaja, "creditoAbono" | "motivo">,
): boolean {
  if (m.creditoAbono) return true;
  // Compatible con motivos nuevos ("Abono a crédito: …") y legados ("Abono fiado: …").
  return /^abono (a crédito|fiado)\s*:/i.test((m.motivo || "").trim());
}

/** Nombre del cliente extraído del motivo «Abono a crédito: …» (o legado «Abono fiado: …»). */
export function clienteNombreAbonoCredito(motivo: string): string {
  const m = motivo.trim().match(/^abono (a crédito|fiado)\s*:\s*(.*)$/i);
  const rest = (m?.[2] ?? motivo).trim();
  return rest || "Cliente";
}

export function metodoPagoAbonoCorto(
  mp: MovimientoCaja["metodoPago"] | string | undefined,
): string {
  const map: Record<string, string> = {
    efectivo: "Efec",
    nequi: "Nequi",
    daviplata: "Davi",
    tarjeta: "Tarj",
    transferencia: "Trans",
  };
  return mp ? (map[mp] ?? String(mp).slice(0, 4)) : "";
}
